/**
 * SupplierQuoteForm — Public supplier portal (no auth required)
 * Route: /sourcing/quote/:token
 * Accessible via tokenized invitation link.
 * Strict whitelist: only shows fields the supplier is allowed to see.
 */
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertCircle, Clock, MapPin, Package, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Use an anonymous axios instance (no auth cookies / headers)
const publicApi = axios.create({ baseURL: API, withCredentials: false });

const STATUS_LABELS = {
  PENDING:           "Awaiting Your Response",
  SENT:              "Response Requested",
  OPENED:            "Link Opened",
  RESPONSE_STARTED:  "Response Started",
  SUBMITTED:         "Response Submitted",
  REVISION_REQUESTED:"Revision Requested",
  EXPIRED:           "Link Expired",
  REVOKED:           "Invitation Withdrawn",
};

function LineItemForm({ item, value, onChange }) {
  const [expanded, setExpanded] = useState(true);
  const set = (field, val) => onChange({ ...value, [field]: val });

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`line-form-${item.id}`}>
      <button
        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        onClick={() => setExpanded(e => !e)}
      >
        <div>
          <p className="font-medium text-slate-800">{item.description}</p>
          <p className="text-sm text-slate-500 mt-0.5">Qty: {item.quantity} {item.unit}</p>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="p-5 space-y-4 bg-white">
          {/* Delivery context (read-only) */}
          {(item.requested_delivery_start || item.requested_delivery_end || item.delivery_location) && (
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-sm space-y-1">
              {item.delivery_location && (
                <p className="flex items-center gap-2 text-indigo-700">
                  <MapPin className="w-3.5 h-3.5" /> {item.delivery_location}
                </p>
              )}
              {(item.requested_delivery_start || item.requested_delivery_end) && (
                <p className="flex items-center gap-2 text-indigo-700">
                  <Clock className="w-3.5 h-3.5" />
                  {item.requested_delivery_start} {item.requested_delivery_end ? `→ ${item.requested_delivery_end}` : ""}
                </p>
              )}
              {item.function_code && <p className="text-indigo-600 text-xs">Function: {item.function_code}</p>}
              {item.delivery_notes && <p className="text-indigo-600 text-xs">{item.delivery_notes}</p>}
            </div>
          )}

          {/* Specifications (read-only) */}
          {item.specifications && Object.keys(item.specifications).length > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm">
              <p className="font-medium text-amber-800 mb-1">Specifications</p>
              {Object.entries(item.specifications).map(([k, v]) => (
                <p key={k} className="text-amber-700 text-xs">{k}: {v}</p>
              ))}
            </div>
          )}

          {/* Quotation fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-600">Unit Price (₹) *</Label>
              <Input
                type="number"
                min="0"
                placeholder="0.00"
                value={value.unit_price || ""}
                onChange={e => {
                  const up = parseFloat(e.target.value) || 0;
                  const qty = parseFloat(item.quantity) || 1;
                  set("unit_price", up);
                  if (up && qty) set("total_amount", parseFloat((up * qty).toFixed(2)));
                }}
                data-testid={`unit-price-${item.id}`}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Total Amount (₹)</Label>
              <Input
                type="number"
                min="0"
                placeholder="Auto-calculated"
                value={value.total_amount || ""}
                onChange={e => set("total_amount", parseFloat(e.target.value) || null)}
                data-testid={`total-amount-${item.id}`}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">GST Applicable</Label>
              <select
                className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={value.gst_applicable ? "yes" : "no"}
                onChange={e => set("gst_applicable", e.target.value === "yes")}
                data-testid={`gst-applicable-${item.id}`}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
            {value.gst_applicable && (
              <div>
                <Label className="text-xs text-slate-600">GST %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="18"
                  value={value.gst_percent || ""}
                  onChange={e => set("gst_percent", parseFloat(e.target.value) || null)}
                  data-testid={`gst-percent-${item.id}`}
                />
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-600">Delivery By</Label>
              <Input
                type="date"
                value={value.delivery_by || ""}
                onChange={e => set("delivery_by", e.target.value)}
                data-testid={`delivery-by-${item.id}`}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Validity Date</Label>
              <Input
                type="date"
                value={value.validity_date || ""}
                onChange={e => set("validity_date", e.target.value)}
                data-testid={`validity-date-${item.id}`}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-600">Payment Terms</Label>
              <Input
                placeholder="e.g. 50% advance, 50% on delivery"
                value={value.payment_terms || ""}
                onChange={e => set("payment_terms", e.target.value)}
                data-testid={`payment-terms-${item.id}`}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-600">Notes / Remarks</Label>
              <Textarea
                placeholder="Any additional details, conditions, or notes…"
                value={value.notes || ""}
                onChange={e => set("notes", e.target.value)}
                rows={2}
                data-testid={`line-notes-${item.id}`}
              />
            </div>
          </div>

          {/* Alternate product */}
          <div className="flex items-start gap-3 pt-1">
            <input
              type="checkbox"
              id={`alt-${item.id}`}
              checked={value.alternate_product_offered || false}
              onChange={e => set("alternate_product_offered", e.target.checked)}
              className="mt-0.5"
              data-testid={`alt-check-${item.id}`}
            />
            <label htmlFor={`alt-${item.id}`} className="text-sm text-slate-700 cursor-pointer">
              I am offering an alternate / equivalent product
            </label>
          </div>
          {value.alternate_product_offered && (
            <div>
              <Label className="text-xs text-slate-600">Alternate Product Description</Label>
              <Textarea
                placeholder="Describe the alternate product you are offering…"
                value={value.alternate_description || ""}
                onChange={e => set("alternate_description", e.target.value)}
                rows={2}
                data-testid={`alt-desc-${item.id}`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SupplierQuoteForm() {
  const { token } = useParams();
  const [view, setView] = useState(null);         // package view from API
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lines, setLines] = useState({});         // { requirement_item_id: {...} }
  const [pkg, setPkg] = useState({ overall_validity_date: "", payment_terms_summary: "", supplier_notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionRevision, setSubmissionRevision] = useState(null);

  useEffect(() => {
    publicApi.get(`/api/sourcing/quote/${token}`)
      .then(res => {
        const data = res.data;
        setView(data);
        // Prefill from previous submission if available
        const initLines = {};
        (data.requirement_items || []).forEach(item => {
          initLines[item.id] = item.prefill || {
            unit_price: null, quantity: item.quantity, total_amount: null,
            gst_applicable: false, gst_percent: null, payment_terms: "",
            delivery_by: "", validity_date: "", notes: "",
            item_specifications: {}, alternate_product_offered: false, alternate_description: "",
          };
        });
        setLines(initLines);
      })
      .catch(err => {
        const detail = err.response?.data?.detail || "This invitation link is invalid or has expired.";
        setError(detail);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    // Validate: at least unit_price on each line
    const lineItems = Object.entries(lines).map(([reqId, vals]) => ({
      requirement_item_id: reqId,
      ...vals,
    }));
    const missing = lineItems.filter(l => !l.unit_price && l.unit_price !== 0);
    if (missing.length > 0) {
      alert("Please enter a unit price for all line items before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await publicApi.post(`/api/sourcing/quote/${token}/submit`, {
        line_items: lineItems,
        overall_validity_date: pkg.overall_validity_date || null,
        payment_terms_summary: pkg.payment_terms_summary || null,
        supplier_notes: pkg.supplier_notes || null,
      });
      setSubmitted(true);
      setSubmissionRevision(res.data?.revision_number);
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to submit quotation. Please try again.";
      alert(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-950 flex items-center justify-center">
        <div className="text-white text-center">
          <Package className="w-12 h-12 mx-auto mb-3 animate-pulse" />
          <p>Loading invitation…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-950 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl" data-testid="quote-error">
          <AlertCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Invitation Not Available</h1>
          <p className="text-slate-600">{error}</p>
          <p className="text-slate-400 text-sm mt-3">If you believe this is an error, please contact the event organiser.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-950 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-lg w-full text-center shadow-2xl" data-testid="quote-success">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Quotation Submitted</h1>
          <p className="text-slate-600 mb-1">
            Thank you! Your quotation (Revision {submissionRevision}) has been received.
          </p>
          <p className="text-slate-500 text-sm">
            The organiser will review your response and be in touch. You may close this page.
          </p>
          <div className="mt-6 p-4 bg-slate-50 rounded-lg text-xs text-slate-400">
            Reference: {token.slice(0, 12)}…
          </div>
        </div>
      </div>
    );
  }

  const isRevision = view?.is_submitted && view?.submission_status === "REVISION_REQUESTED";
  const canSubmit = view?.can_resubmit;
  const expired = view?.invitation_status === "EXPIRED" || view?.invitation_status === "REVOKED";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-950 py-10 px-4" data-testid="supplier-quote-form">
      <div className="max-w-3xl mx-auto">
        {/* Header card */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Quotation Invitation</p>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="quote-package-title">{view?.package_title}</h1>
              {view?.supplier_name && (
                <p className="text-slate-500 mt-1">For: <span className="font-medium text-slate-700">{view.supplier_name}</span></p>
              )}
            </div>
            <Badge className={`${view?.invitation_status === "SUBMITTED" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"} border-0`} data-testid="quote-inv-status">
              {STATUS_LABELS[view?.invitation_status] || view?.invitation_status}
            </Badge>
          </div>

          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-slate-100 text-sm">
            {view?.service_category_name && (
              <div><span className="text-slate-400">Category</span><p className="font-medium text-slate-700">{view.service_category_name}</p></div>
            )}
            {view?.delivery_context?.delivery_location && (
              <div><span className="text-slate-400">Location</span><p className="font-medium text-slate-700">{view.delivery_context.delivery_location}</p></div>
            )}
            {view?.delivery_context?.delivery_start && (
              <div><span className="text-slate-400">Delivery Window</span><p className="font-medium text-slate-700">{view.delivery_context.delivery_start} → {view.delivery_context.delivery_end || "TBD"}</p></div>
            )}
            {view?.response_deadline && (
              <div><span className="text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Deadline</span><p className="font-medium text-slate-700">{view.response_deadline}</p></div>
            )}
          </div>

          {isRevision && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                A revision has been requested. Please review and resubmit your quotation. Revision {(view?.current_revision || 0) + 1} will supersede your previous submission.
              </p>
            </div>
          )}

          {expired && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">This invitation link has expired or been withdrawn. Please contact the organiser.</p>
            </div>
          )}
        </div>

        {/* Line items */}
        {!expired && canSubmit && (
          <>
            <div className="space-y-4 mb-6">
              <h2 className="text-white font-semibold text-lg">Items to Quote</h2>
              {(view?.requirement_items || []).map(item => (
                <LineItemForm
                  key={item.id}
                  item={item}
                  value={lines[item.id] || {}}
                  onChange={vals => setLines(l => ({ ...l, [item.id]: vals }))}
                />
              ))}
            </div>

            {/* Package-level fields */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h3 className="font-semibold text-slate-900 mb-4">Package-Level Terms</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-600">Overall Quotation Validity Date</Label>
                  <Input type="date" value={pkg.overall_validity_date} onChange={e => setPkg(p => ({...p, overall_validity_date: e.target.value}))} className="mt-1" data-testid="pkg-validity-date" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Overall Payment Terms Summary</Label>
                  <Textarea
                    placeholder="e.g. 50% advance on PO, balance 7 days before event"
                    value={pkg.payment_terms_summary}
                    onChange={e => setPkg(p => ({...p, payment_terms_summary: e.target.value}))}
                    rows={2}
                    className="mt-1"
                    data-testid="pkg-payment-terms"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Additional Notes / Remarks</Label>
                  <Textarea
                    placeholder="Any overall notes, setup charges, transport charges, or conditions…"
                    value={pkg.supplier_notes}
                    onChange={e => setPkg(p => ({...p, supplier_notes: e.target.value}))}
                    rows={3}
                    className="mt-1"
                    data-testid="pkg-supplier-notes"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Ready to submit?</p>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {isRevision
                      ? `This will be Revision ${(view?.current_revision || 0) + 1}. Your previous submission is preserved.`
                      : "Your quotation will be reviewed by the organiser. You cannot modify it after submission unless a revision is requested."}
                  </p>
                </div>
                <Button
                  size="lg"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="ml-4 shrink-0"
                  data-testid="submit-quote-btn"
                >
                  {submitting ? "Submitting…" : isRevision ? "Submit Revision" : "Submit Quotation"}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Already submitted, no revision requested */}
        {view?.is_submitted && !canSubmit && !expired && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="quote-already-submitted">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Quotation Received</h2>
            <p className="text-slate-600">
              You have submitted Revision {view?.current_revision}. The organiser will review it.
            </p>
            <p className="text-slate-400 text-sm mt-2">
              If the organiser requests changes, you will be able to resubmit.
            </p>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-8">
          Powered by Prashasta Events Intelligence · This is a supplier-only secure portal
        </p>
      </div>
    </div>
  );
}
